import React, { createContext, useContext, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getAuthHeaders, getImpersonationIdentityKey, makeApiCall } from '../../utils/apiConfig';
import { getActingOrganizationId } from '../../utils/actingOrganization';
import { useAuth } from '../../contexts/AuthContext';
import { useOrgFeatureFlag } from '../../hooks/useOrgFeatureFlags';
import type { TrialStatusResponse } from './trialChipModel';

export const TRIAL_STATUS_QUERY_KEY = ['billing-trial-status'] as const;

/** Re-read the trial this often, so a grant an admin changes shows up. */
const TRIAL_STATUS_REFRESH_MS = 15 * 60_000;

/** A timer longer than this overflows `setTimeout`; a longer wait re-arms. */
const MAX_TIMER_MS = 2 ** 31 - 1;

const TrialStatusContext = createContext<TrialStatusResponse | null>(null);

/**
 * True once `endsAt` has passed, re-rendering the provider at that moment so
 * the badges clear with the trial instead of at the next 15-minute read.
 */
const useTrialEnded = (endsAt: string | null | undefined): boolean => {
  const end = endsAt ? new Date(endsAt).getTime() : NaN;
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    if (Number.isNaN(end)) return undefined;
    const wait = end - Date.now();
    if (wait <= 0) {
      // Once only: `nowMs` is past `end` after this, and the effect settles.
      if (nowMs < end) setNowMs(Date.now());
      return undefined;
    }
    const timer = window.setTimeout(() => setNowMs(Date.now()), Math.min(wait + 250, MAX_TIMER_MS));
    return () => window.clearTimeout(timer);
  }, [end, nowMs]);
  return !Number.isNaN(end) && nowMs >= end;
};

/**
 * Reads `GET /billing/trial-status` once for the shell (flag
 * `store_trial_countdown`) and hands it to the top-bar chip and every trial
 * tier badge below it. `enabled` is the store shell only.
 */
export const TrialStatusProvider: React.FC<{ enabled: boolean; children: React.ReactNode }> = ({
  enabled: shellEnabled,
  children,
}) => {
  const { currentUser } = useAuth();
  const uid = currentUser?.uid ?? null;
  const { enabled: flagOn } = useOrgFeatureFlag('store_trial_countdown');
  const enabled = shellEnabled && flagOn && Boolean(uid);

  const { data } = useQuery({
    enabled,
    // The app's QueryClient outlives a sign-out, so the key names whoever is
    // asking: the account, the store it is acting for, and any View-as.
    queryKey: [...TRIAL_STATUS_QUERY_KEY, uid, getActingOrganizationId(uid), getImpersonationIdentityKey()],
    queryFn: async (): Promise<TrialStatusResponse | null> => {
      const headers = await getAuthHeaders();
      const response = await makeApiCall('/billing/trial-status', { headers });
      if (!response.ok) return null;
      const body = await response.json();
      return body?.data ?? null;
    },
    staleTime: 5 * 60_000,
    refetchInterval: TRIAL_STATUS_REFRESH_MS,
  });

  const ended = useTrialEnded(data?.trial?.endsAt);
  const value = !enabled || !data ? null : ended ? { ...data, trial: null } : data;

  return <TrialStatusContext.Provider value={value}>{children}</TrialStatusContext.Provider>;
};

/**
 * The store's trial, or null outside a trial, outside the store shell, or
 * where no `TrialStatusProvider` is mounted (a card rendered on its own).
 */
export const useTrialStatus = (): TrialStatusResponse | null => useContext(TrialStatusContext);
